import { Component } from '../lib/ComponentBase';
import { simpleComponent } from '../lib/ComponentHelpers';
import type { IntentionProvider, PathfindingNode, WorldState } from '../types/Components';
import type { Entity } from '../types/Entity';
import type { Intention } from '../types/AI';
import { isAttackPositionProvider, isMoveProvider } from '../types/Components';
import { HexPosition, MoveForward, Turn } from '.';
import Swivel from './Swivel';
import { HexBearing } from '../grid';
import { ActionType } from '../lib/Action';
import Pathfinder from '../lib/Pathfinder';

/**
 * An AI behavior component that uses pathfinding to position itself
 * to use its abilities. Its behavior emerges from the "goal provider"
 * components on the same entity (e.g. attacks).
 */
class PathfindingAI extends Component implements IntentionProvider {
  getIntention(entity: Entity, worldState: WorldState): Intention | null {
    const { player, walkableCoords, occupiedCoords } = worldState;
    if (!player) return null;

    const aiPosition = entity.getComponent(HexPosition);
    if (!aiPosition) return null;

    const goalPositions: PathfindingNode[] = [];
    // Assumption: entity.components is a Map of component instances, based on mocks.
    if (entity.getAllComponents()) {
      for (const component of entity.getAllComponents()) {
        if (isAttackPositionProvider(component)) {
          const positions = component.getAttackPositions(entity, worldState);
          if (positions) {
            goalPositions.push(...positions);
          }
        }
      }
    }

    if (goalPositions.length === 0) {
      return null;
    }

    // TODO do we need a better way to determine "passable" here?
    // it should be based on faction -- you can't move through your own
    // "teammate" but you can attempt to move "into" your enemy.
    const passableCoords = new Set(walkableCoords);
    for (const [key, occupant] of occupiedCoords.entries()) {
      if (occupant.id !== entity.id && occupant.id !== player.id) {
        passableCoords.delete(key);
      }
    }

    const getNeighborsFn = (node: PathfindingNode): PathfindingNode[] => {
      const neighbors: PathfindingNode[] = [];
      const worldStateForMoves = { walkableCoords: passableCoords };

      for (const component of entity.getAllComponents()) {
        if (isMoveProvider(component)) {
          const moves = component.getPossibleMoves(node, worldStateForMoves);
          neighbors.push(...moves);
        }
      }
      return neighbors;
    };

    const startNode = { coords: aiPosition.coords, bearing: aiPosition.bearing };
    const path = Pathfinder.findPath(
      startNode,
      goalPositions,
      getNeighborsFn
    );

    if (!path || path.length <= 1) {
      return null; // No path or already at a goal
    }

    const nextStep = path[1];


    // Is it a move?
    if (!nextStep.coords.is(aiPosition.coords)) {
      return {
        priority: 10,
        type: ActionType.MOVE,
        details: { coords: nextStep.coords },
        sourceComponentName: 'PathfindingAI',
      };
    }

    // Is it a turn?
    if (nextStep.bearing !== aiPosition.bearing) {
      return {
        priority: 10,
        type: ActionType.TURN,
        details: { bearing: nextStep.bearing },
        sourceComponentName: 'PathfindingAI',
      };
    }

    return null;
  }
}

export default simpleComponent(PathfindingAI);
